'use client';

import { datadogRum } from '@datadog/browser-rum';

import { isBot } from '@/utils';
import { pageTemplateFromUrl } from '@/utils/datadog-page-template';

/**
 * 노이즈 에러 필터링 패턴
 * - IgnoredEventCancel: Chrome 브라우저 intervention (passive touch event)
 * - Object Not Found Matching Id: MS 이메일 링크 스캐너(SafeLinks류) 임베디드 브라우저 에러
 */
const IGNORED_ERROR_PATTERNS = [
  // Chrome passive event listener intervention
  'ignoredeventcancel',
  'cancel a touchend event with cancelable=false',
  // MS 이메일 링크 스캐너 (위장 UA로 isBot 게이팅을 통과하므로 메시지 패턴으로 폐기)
  'object not found matching id',
] as const;

const isIgnoredError = (message: string): boolean => {
  const lowerMessage = message.toLowerCase();
  return IGNORED_ERROR_PATTERNS.some(pattern => lowerMessage.includes(pattern));
};

const syncTrackingConsentWithCookiebot = (): void => {
  datadogRum.setTrackingConsent(
    window.Cookiebot?.consent?.statistics ? 'granted' : 'not-granted',
  );
};

// Datadog RUM은 세션 수에 비례해 과금되므로 봇 세션은 init 단계에서 차단한다.
// 브라우저 전용 SDK이므로 SSR 단계에서는 init 하지 않는다.
if (typeof window !== 'undefined' && !isBot()) {
  datadogRum.init({
    applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID as string,
    clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN as string,
    site: 'datadoghq.com',
    service: 'homepage-front',
    env: process.env.NEXT_PUBLIC_PHASE === 'prod' ? 'prod' : 'dev',
    version: process.env.NEXT_PUBLIC_DATADOG_VERSION,
    sessionSampleRate: parseFloat(
      process.env.NEXT_PUBLIC_DATADOG_SESSION_SAMPLE_RATE ?? '100',
    ),
    sessionReplaySampleRate: parseFloat(
      process.env.NEXT_PUBLIC_DATADOG_SESSION_REPLAY_SAMPLE_RATE ?? '20',
    ),
    trackingConsent: 'not-granted',
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [url => url.startsWith(window.location.origin)],
    beforeSend: event => {
      if (event.type === 'error') {
        const message = event.error?.message ?? '';
        if (isIgnoredError(message)) return false;
      }
      // LCP 메트릭 page dimension. useEffect 는 view 시작보다 늦어 메트릭이 N/A 로
      // 기록되므로, view URL 로부터 동기 계산해 모든 이벤트에 붙인다.
      const viewUrl = event.view?.url;
      if (viewUrl) {
        event.context = {
          ...event.context,
          page_template: pageTemplateFromUrl(viewUrl),
        };
      }
      return true;
    },
  });

  syncTrackingConsentWithCookiebot();
  window.addEventListener(
    'CookiebotOnConsentReady',
    syncTrackingConsentWithCookiebot,
  );
  window.addEventListener(
    'CookiebotOnAccept',
    syncTrackingConsentWithCookiebot,
  );
  window.addEventListener(
    'CookiebotOnDecline',
    syncTrackingConsentWithCookiebot,
  );
}

export default function DatadogInit() {
  return null;
}
